import { AlertCircle, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { ColocaTabs, type ColocaTabItem } from "@/shared/components/navigation/ColocaTabs";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { colocaToast } from "@/shared/utils/toast";

import { CancelPayoutModal } from "../components/CancelPayoutModal";
import { RescueDrawer } from "../components/RescueDrawer";
import { RescueFilters } from "../components/RescueFilters";
import { RescueKpiStrip } from "../components/RescueKpiStrip";
import { RescueTable } from "../components/RescueTable";
import { RetryPayoutModal } from "../components/RetryPayoutModal";
import { usePayoutRescueQueue } from "../hooks/usePayoutRescueQueue";
import { PayoutStatus } from "../services/payoutRescue.service";
import type { RescuePayout, RescueTab, RetryOutcome } from "../types/payoutRescue.types";
import { buildRescueCsv, downloadCsv } from "../utils/payoutRescueFormat";

/**
 * `/app/payout-rescue` — INTERNAL ops rescue queue for payouts whose automatic dispatch failed (stuck in
 * CREATED with the funds still reserved): retry the send or cancel to return the funds. The route is gated to
 * userTypes: [INTERNAL] by RequireAccess; the RBAC part (`payments:read`/`payments:write`) is decided by the
 * backend and a 401/403 latches into the no-access view — legacy scopes are NOT consulted.
 *
 * Freshness (design brief §5): NO polling anywhere — data refreshes only on the manual button, tab changes
 * and action responses (dispatch/cancel already return the fresh row).
 */
const PayoutRescuePage = () => {
  const { t } = useTranslations("payoutRescue");
  const { t: tGlobal } = useTranslations("global");

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_operaciones") },
      { label: tGlobal("sidebar_menu_payout_rescue"), to: "/app/payout-rescue" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  // Org names for the table/drawer/filters (list rows only carry `organization_id`). A denied/failed
  // catalog degrades to shortened uuids — it never blocks the rescue flow.
  const { organizations } = useOrganizationsCatalog();
  const orgNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of organizations) map[org.id] = org.name;
    return map;
  }, [organizations]);
  const orgName = useCallback((id: string) => orgNameById[id] ?? null, [orgNameById]);

  const {
    tab,
    setTab,
    filters,
    setSearch,
    setOrganizationId,
    setCountry,
    historyStatus,
    setHistoryStatus,
    clearFilters,
    hasActiveFilters,
    rows,
    historyPage,
    setHistoryPage,
    historyTotal,
    isQueueTruncated,
    kpis,
    isLoading,
    isRefreshing,
    hasError,
    hasAccess,
    refresh,
    applyActionResult,
  } = usePayoutRescueQueue(orgNameById);

  const [selected, setSelected] = useState<RescuePayout | null>(null);
  const [retryTarget, setRetryTarget] = useState<RescuePayout | null>(null);
  const [cancelTarget, setCancelTarget] = useState<RescuePayout | null>(null);

  const handleRetryResolved = useCallback(
    (outcome: RetryOutcome) => {
      setRetryTarget(null);
      if (outcome.kind === "stuck") {
        // Pre-claim config error: the payout is untouched (still CREATED) — keep the drawer open so the
        // operator can retry after platform fixes the corridor, or cancel to return the funds.
        colocaToast.warning(t("toast_retry_stuck"));
        return;
      }
      applyActionResult(outcome.payout);
      setSelected(null);
      switch (outcome.kind) {
        case "paid":
          colocaToast.success(t("toast_retry_paid"));
          break;
        case "processing":
          colocaToast.success(t("toast_retry_processing"));
          break;
        case "failed":
          colocaToast.warning(t("toast_retry_failed"));
          break;
        case "race":
          colocaToast.info(t("toast_race"));
          break;
      }
    },
    [applyActionResult, t],
  );

  const handleCancelResolved = useCallback(
    (payout: RescuePayout, wasRace: boolean) => {
      setCancelTarget(null);
      applyActionResult(payout);
      setSelected(null);
      if (wasRace) colocaToast.info(t("toast_race"));
      else colocaToast.success(t("toast_cancelled"));
    },
    [applyActionResult, t],
  );

  const handleExport = useCallback(() => {
    const csv = buildRescueCsv(rows, (id) => orgName(id) ?? id);
    downloadCsv(csv, `payout-rescue-${tab}-${new Date().toISOString().slice(0, 10)}.csv`);
  }, [rows, orgName, tab]);

  if (!hasAccess) {
    return <NoAccessView />;
  }

  const tabItems: ColocaTabItem[] = [
    { value: "pending", label: `${t("tab_pending")} (${kpis.pending})`, content: null },
    { value: "processing", label: `${t("tab_processing")} (${kpis.processing})`, content: null },
    { value: "history", label: t("tab_history"), content: null },
  ];

  return (
    <div className="space-y-6 p-2">
      {/* Title + manual refresh (the ONLY on-demand refresh — no polling on this surface) */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t("page_title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("page_subtitle")}</p>
        </div>
        <ColocaButton
          variant="default"
          size="sm"
          icon={RefreshCw}
          aria-label={t("refresh_aria")}
          className={cn(isRefreshing && "[&_svg]:animate-spin")}
          onClick={() => void refresh()}
        >
          {t("refresh_button")}
        </ColocaButton>
      </div>

      <RescueKpiStrip kpis={kpis} />

      <ColocaTabs tabs={tabItems} value={tab} onValueChange={(v) => setTab(v as RescueTab)} />

      <RescueFilters
        tab={tab}
        filters={filters}
        onSearchChange={setSearch}
        onOrganizationChange={setOrganizationId}
        onCountryChange={setCountry}
        historyStatus={historyStatus}
        onHistoryStatusChange={setHistoryStatus}
        onClear={clearFilters}
        hasActiveFilters={hasActiveFilters}
        organizations={organizations}
        onExport={handleExport}
        canExport={rows.length > 0}
      />

      {isQueueTruncated && (
        <ColocaAlert
          variant="warning"
          text={t("queue_truncated", {
            shown: rows.length,
            total: tab === "pending" ? kpis.pending : kpis.processing,
          })}
        />
      )}

      {/* Fetch failure: explicit error + retry — never fabricated/zeroed rows. */}
      {hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {t("error_load")}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={() => void refresh()}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      ) : (
        <RescueTable
          tab={tab}
          rows={rows}
          orgName={orgName}
          isLoading={isLoading}
          onRowClick={setSelected}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
          historyPage={historyPage}
          historyTotal={historyTotal}
          onHistoryPageChange={setHistoryPage}
        />
      )}

      {selected && (
        <RescueDrawer
          payout={selected}
          orgName={orgName(selected.organization_id)}
          open={selected !== null}
          onClose={() => setSelected(null)}
          onRetry={(fresh) => {
            // FAILED can never be re-dispatched (state machine) — the drawer only offers retry on CREATED,
            // and this guard keeps it true even against a stale row.
            if (fresh.status === PayoutStatus.CREATED) setRetryTarget(fresh);
          }}
          onCancel={(fresh) => {
            if (fresh.status === PayoutStatus.CREATED) setCancelTarget(fresh);
          }}
        />
      )}

      {retryTarget && (
        <RetryPayoutModal
          payout={retryTarget}
          orgName={orgName(retryTarget.organization_id)}
          open={retryTarget !== null}
          onClose={() => setRetryTarget(null)}
          onResolved={handleRetryResolved}
        />
      )}

      {cancelTarget && (
        <CancelPayoutModal
          payout={cancelTarget}
          orgName={orgName(cancelTarget.organization_id)}
          open={cancelTarget !== null}
          onClose={() => setCancelTarget(null)}
          onResolved={handleCancelResolved}
        />
      )}
    </div>
  );
};

export default PayoutRescuePage;
